import bulkUpdatePriceList from '@/api/patch/company/company-price/bulk-update-price-list';
import bulkUpdateDealerPriceList from '@/api/patch/dealer/bulk-update-dealer-price-list';
import SubmitCard from '@/components/card/submit-card-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import { useMutation } from '@/hooks/use-api';
import { Subtitle2Text } from '@/utils/global-fonts';
import { removeUndefinedProperties } from '@/utils/utils';
import { requiredValidationMessage } from '@/utils/validations';
import PropTypes from 'prop-types';
import ReactDOMServer from 'react-dom/server';
import { FiPercent } from 'react-icons/fi';
import Swal from 'sweetalert2';

const modalRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
};

const modalParentRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
  padding: '24px',
};

const styledCol = {
  paddingLeft: '0px',
};

/**
 * @param {object} root0 - The root object containing props
 * @param {Function} root0.onClose - Callback function to close the modal
 * @param {Function} root0.refetch - Callback function to refetch the data
 * @param {number} root0.tableCount - number of items in table to update
 * @param {string} root0.companyId - company / dealer Id to update priceList
 * @param {bool} root0.isDealer - if dealer company
 * @returns {JSX.Element} - The rendered JSX element
 */
const EditGlobalMarkupModal = ({
  isDealer,
  companyId,
  onClose,
  refetch,
  tableCount,
}) => {
  const [bulkEditDealerPricing, { loading: bulkEditDealerPriceLoading }] =
    useMutation(bulkUpdateDealerPriceList);

  const [bulkEditPricing, { loading: bulkEditPriceLoading }] =
    useMutation(bulkUpdatePriceList);

  const submitForm = (values) => {
    if (values) {
      ConfirmationModal({
        title: 'Confirm Global Markup',
        text: `This change will apply to ${tableCount} products. Are you sure you want to proceed?`,
        onConfirm: () => {
          const submitValues = {
            price: {
              end_user_gross_profit: parseFloat(
                values.price.end_user_gross_profit,
              ),
              transfer_price_gross_profit: parseFloat(
                values.price.transfer_price_gross_profit,
              ),
            },
            priceIds: 'all',
          };
          if (isDealer) {
            bulkEditDealerPricing(
              companyId,
              removeUndefinedProperties(submitValues),
            ).then((re) => {
              Swal.fire({
                title: 'Import Summary',
                html: ReactDOMServer.renderToString(
                  <span>{`${re.data} additional products have been updated`}</span>,
                ),
                showCloseButton: true,
                showCancelButton: false,
                confirmButtonText: 'Ok',
                showLoaderOnConfirm: true,
              });
              onClose();
              refetch();
            });
          } else {
            bulkEditPricing(
              companyId,
              removeUndefinedProperties(submitValues),
            ).then((re) => {
              onClose();
              refetch();
              Swal.fire({
                title: 'Import Summary',
                html: ReactDOMServer.renderToString(
                  <span>{`${re.data} additional products have been updated`}</span>,
                ),
                showCloseButton: true,
                showCancelButton: false,
                confirmButtonText: 'Ok',
                showLoaderOnConfirm: true,
              });
            });
          }
        },
      });
    }
  };

  return (
    <FormWrapper defaultValues={{}} onSubmit={submitForm}>
      <Row style={modalParentRowStyle}>
        {bulkEditPriceLoading || bulkEditDealerPriceLoading ? (
          <Loader loading loadingText="...Loading" />
        ) : (
          <>
            <Row>
              <Col xs={12}>
                <Subtitle2Text>End User Gross Profit</Subtitle2Text>
              </Col>
            </Row>
            <Row style={modalRowStyle}>
              <Col style={styledCol} sm={12} md={12} lg={12} xl={12} xxl={12}>
                <Input
                  disabled={false}
                  iconRight={<FiPercent />}
                  label="Gross Profit"
                  type="number"
                  placeholder="Enter End User Gross Profit"
                  name="price.end_user_gross_profit"
                  registerOptions={{
                    required: requiredValidationMessage,
                    valueAsNumber: true,
                    max: 99.99,
                    min: 0,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Subtitle2Text>Transfer Price Gross Profit</Subtitle2Text>
              </Col>
            </Row>
            <Row style={modalRowStyle}>
              <Col style={styledCol} sm={12} md={12} lg={12} xl={12} xxl={12}>
                <Input
                  disabled={false}
                  iconRight={<FiPercent />}
                  label="Gross Profit"
                  type="number"
                  placeholder="Enter Transfer Price Gross Profit"
                  name="price.transfer_price_gross_profit"
                  registerOptions={{
                    required: requiredValidationMessage,
                    valueAsNumber: true,
                    max: 99.99,
                    min: 0,
                  }}
                />
              </Col>
            </Row>
          </>
        )}
      </Row>
      <SubmitCard
        padding="0"
        primaryButtonTitle="Apply to all"
        primaryButtonType="submit"
        primaryButtonDisabled={false}
        primaryButtonWidth="112px"
        submitClick={() => {}}
        submitClickSecondary={() => {
          onClose();
        }}
        secondaryButtonType="button"
        secondaryButtonDisabled={false}
        secondaryButtonWidth="112px"
        disabled={false}
        secondaryButtonTitle="Cancel"
      />
    </FormWrapper>
  );
};

EditGlobalMarkupModal.defaultProps = {
  tableCount: 0,
};

EditGlobalMarkupModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  tableCount: PropTypes.number,
  companyId: PropTypes.string.isRequired,
  isDealer: PropTypes.bool.isRequired,
};

export default EditGlobalMarkupModal;
